import { useTranslation } from "react-i18next"

import type {
  ActionItem,
  AIRecommendation,
  BidDeliverable,
  ProcurementOverview,
  RecommendationReason,
  ScorecardDimension,
} from "../../types"

import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  ClipboardList,
  ExternalLink,
  TrendingDown,
  TrendingUp,
  XCircle,
} from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { cn } from "@/lib/utils"

import { EvidenceCell } from "../evidence-cell"
import { Scorecard } from "../scorecard"
import { SnabbanalysCard } from "../snabbanalys-card"
import { TenderAiDisclaimer } from "../tender-ai-disclaimer"

const VERDICT_STYLE = {
  go: {
    icon: CheckCircle2,
    badgeClass: "bg-success text-success-foreground border border-foreground",
    bannerClass: "border-success/30 bg-success/[0.06]",
    labelClass: "text-success",
    accentClass: "text-success",
  },
  no_go: {
    icon: XCircle,
    badgeClass: "bg-destructive text-destructive-foreground border border-foreground",
    bannerClass: "border-destructive/30 bg-destructive/[0.06]",
    labelClass: "text-destructive",
    accentClass: "text-destructive",
  },
  go_med_forbehall: {
    icon: AlertTriangle,
    badgeClass: "bg-warning text-warning-foreground border border-foreground",
    bannerClass: "border-warning/30 bg-warning/[0.06]",
    labelClass: "text-warning-foreground",
    accentClass: "text-warning",
  },
} as const

type ReasonListItemProps = {
  reason: RecommendationReason
  icon: React.ReactNode
  textClassName?: string
}

function ReasonListItem({ reason, icon, textClassName }: ReasonListItemProps) {
  return (
    <li className="space-y-2">
      <div className={cn("flex items-start gap-2 text-sm leading-snug", textClassName)}>
        <span className="mt-0.5 shrink-0">{icon}</span>
        <span>{reason.text}</span>
      </div>
      <div className="flex flex-wrap items-center gap-1.5 pl-5">
        <EvidenceCell evidence={reason.evidence} />
      </div>
    </li>
  )
}

type ArgumentColumnProps = {
  title: string
  count: number
  icon: React.ReactNode
  headerClass: string
  borderClass: string
  reasons: RecommendationReason[]
  reasonIcon: React.ReactNode
  reasonTextClass?: string
  emptyLabel: string
}

function ArgumentColumn({
  title,
  count,
  icon,
  headerClass,
  borderClass,
  reasons,
  reasonIcon,
  reasonTextClass,
  emptyLabel,
}: ArgumentColumnProps) {
  return (
    <Card className={cn("overflow-hidden", borderClass)}>
      <CardHeader className="pt-4 pb-3">
        <CardTitle
          className={cn(
            "flex items-center gap-2 text-xs font-semibold tracking-wide uppercase",
            headerClass
          )}
        >
          {icon}
          {title}
          <span className="ml-auto font-normal opacity-60">{count}</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {reasons.length === 0 ? (
          <p className="text-muted-foreground text-xs">{emptyLabel}</p>
        ) : (
          <ul className="space-y-4">
            {reasons.map((reason, i) => (
              <ReasonListItem
                key={i}
                icon={reasonIcon}
                reason={reason}
                textClassName={reasonTextClass}
              />
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  )
}

const ACTION_PRIORITY_STYLE = {
  hog: { className: "border-destructive bg-destructive/10 text-destructive" },
  medel: { className: "border-warning bg-warning/10 text-warning-foreground" },
  lag: { className: "border-border bg-muted/50 text-muted-foreground" },
} as const

function ActionItemsList({ items }: { items: ActionItem[] }) {
  const { t } = useTranslation()
  if (items.length === 0) return null

  const sorted = [...items].sort((a, b) => {
    const order = { hog: 0, medel: 1, lag: 2 }
    return order[a.priority] - order[b.priority]
  })

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-muted-foreground flex items-center gap-1.5 text-xs font-semibold tracking-wide uppercase">
          <ArrowRight className="size-3.5" />
          {t("tender.sections.recommendation.actionItemsTitle")}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <ul className="divide-border divide-y">
          {sorted.map((item) => {
            const priorityConf = ACTION_PRIORITY_STYLE[item.priority]
            return (
              <li key={item.id} className="flex items-start gap-3 py-3 first:pt-0 last:pb-0">
                <Badge
                  className={cn("mt-0.5 shrink-0 text-xs font-normal", priorityConf.className)}
                  variant="outline"
                >
                  {t(`tender.sections.recommendation.priority.${item.priority}`)}
                </Badge>
                <div className="min-w-0 flex-1">
                  <p className="text-sm">{item.text}</p>
                  {item.relatedIds && item.relatedIds.length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {item.relatedIds.map((id) => (
                        <span
                          key={id}
                          className="bg-muted text-muted-foreground rounded px-1.5 py-0.5 font-mono text-[10px]"
                        >
                          {id}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </li>
            )
          })}
        </ul>
      </CardContent>
    </Card>
  )
}

function BidDeliverablesCard({
  items,
  onGoToExtraction,
}: {
  items: BidDeliverable[]
  onGoToExtraction?: () => void
}) {
  const { t } = useTranslation()
  if (items.length === 0) return null

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-muted-foreground flex items-center gap-1.5 text-xs font-semibold tracking-wide uppercase">
            <ClipboardList className="size-3.5" />
            {t("tender.sections.recommendation.bidContent.title")}
          </CardTitle>
          {onGoToExtraction && (
            <Button
              className="text-muted-foreground hover:text-foreground h-auto gap-1 p-0 text-xs"
              type="button"
              variant="link"
              onClick={onGoToExtraction}
            >
              <ExternalLink className="size-3" />
              {t("tender.sections.recommendation.bidContent.seeSources")}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <ul className="divide-border divide-y">
          {items.map((item) => (
            <li key={item.id} className="flex items-center gap-3 py-2.5 first:pt-0 last:pb-0">
              <div className="min-w-0 flex-1">
                <p className="text-sm">{item.name}</p>
                {item.description && (
                  <p className="text-muted-foreground mt-0.5 text-xs">{item.description}</p>
                )}
              </div>
              <Badge
                variant="outline"
                className={cn(
                  "shrink-0 border text-xs font-normal",
                  item.isRequired
                    ? "border-border bg-muted/50 text-foreground"
                    : "border-border bg-muted/50 text-muted-foreground"
                )}
              >
                {item.isRequired
                  ? t("tender.extraction.deliverableMandatory")
                  : t("tender.extraction.deliverableVoluntary")}
              </Badge>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  )
}

type RecommendationSectionProps = {
  scorecardDimensions: ScorecardDimension[]
  recommendation: AIRecommendation
  procurementOverview?: ProcurementOverview
  /** The case's "Sista dag för frågor" (YYYY-MM-DD), when one was entered. */
  questionsDeadline?: string | null
  bidDeliverables?: BidDeliverable[]
  onGoToExtraction?: () => void
}

export function RecommendationSection({
  scorecardDimensions,
  recommendation,
  procurementOverview,
  questionsDeadline = null,
  bidDeliverables,
  onGoToExtraction,
}: RecommendationSectionProps) {
  const { t } = useTranslation()
  const overallScore = Math.round(
    scorecardDimensions.reduce((sum, d) => sum + d.score, 0) / scorecardDimensions.length
  )

  const conf = VERDICT_STYLE[recommendation.verdict]
  const VerdictIcon = conf.icon
  const verdictLabel = t(`tender.sections.recommendation.verdicts.${recommendation.verdict}`)
  const verdictDescription = t(
    `tender.sections.recommendation.verdictDescriptions.${recommendation.verdict}`
  )

  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-base font-semibold">{t("tender.sections.recommendation.title")}</h3>
        <p className="text-muted-foreground text-sm">
          {t("tender.sections.recommendation.subtitle")}
        </p>
      </div>

      <TenderAiDisclaimer />

      {/* Hero row: procurement overview + recommendation verdict */}
      <div className="grid gap-5 lg:grid-cols-2">
        {/* Procurement overview */}
        {procurementOverview ? (
          <SnabbanalysCard overview={procurementOverview} questionsDeadline={questionsDeadline} />
        ) : (
          <Card>
            <CardContent className="pt-6">
              <p className="text-muted-foreground text-sm">
                {t("tender.sections.recommendation.procurementPlaceholder")}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Recommendation verdict */}
        <Card className={cn("overflow-hidden border-2", conf.bannerClass)}>
          <CardHeader className="pb-3">
            <CardTitle className="text-muted-foreground flex items-center gap-1.5 text-xs font-semibold tracking-wide uppercase">
              AI-rekommendation
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 pt-0">
            {/* Verdict row */}
            <div className="flex items-center gap-3">
              <VerdictIcon className={cn("size-9 shrink-0", conf.labelClass)} />
              <div className="min-w-0">
                <p className={cn("text-xl leading-tight font-bold", conf.labelClass)}>
                  {verdictLabel}
                </p>
                <p className="text-muted-foreground text-xs">{verdictDescription}</p>
              </div>
              <div className="ml-auto shrink-0 text-right">
                <p className={cn("text-3xl leading-none font-bold tabular-nums", conf.labelClass)}>
                  {recommendation.confidence}%
                </p>
                <p className="text-muted-foreground mt-0.5 text-[10px] tracking-wide uppercase">
                  {t("tender.sections.recommendation.confidence")}
                </p>
              </div>
            </div>

            {/* Verdict badge */}
            <Badge className={cn("w-fit text-xs font-semibold", conf.badgeClass)}>
              {verdictLabel}
            </Badge>

            {/* Full summary text */}
            <p className="text-muted-foreground text-sm leading-relaxed">
              {recommendation.summary}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Scorecard — full width */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-sm font-semibold">
            {t("tender.sections.recommendation.scorecard")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Scorecard dimensions={scorecardDimensions} overallScore={overallScore} />
        </CardContent>
      </Card>

      {/* Arguments — three columns */}
      <div className="grid gap-4 lg:grid-cols-3">
        <ArgumentColumn
          borderClass="border-success"
          count={recommendation.reasonsFor.length}
          emptyLabel={t("tender.sections.recommendation.emptyFor")}
          headerClass="text-success"
          icon={<TrendingUp className="size-3.5" />}
          reasonIcon={<CheckCircle2 className="text-success size-3.5" />}
          reasons={recommendation.reasonsFor}
          title={t("tender.sections.recommendation.sections.for")}
        />
        <ArgumentColumn
          borderClass="border-destructive/30"
          count={recommendation.reasonsAgainst.length}
          emptyLabel={t("tender.sections.recommendation.emptyAgainst")}
          headerClass="text-destructive"
          icon={<TrendingDown className="size-3.5" />}
          reasonIcon={<XCircle className="text-destructive size-3.5" />}
          reasons={recommendation.reasonsAgainst}
          title={t("tender.sections.recommendation.sections.against")}
        />
        <ArgumentColumn
          borderClass="border-warning/30"
          count={recommendation.uncertainties.length}
          emptyLabel={t("tender.sections.recommendation.emptyUncertainties")}
          headerClass="text-warning"
          icon={<AlertTriangle className="size-3.5" />}
          reasonIcon={<AlertTriangle className="text-warning size-3.5" />}
          reasons={recommendation.uncertainties}
          reasonTextClass="text-warning-foreground"
          title={t("tender.sections.recommendation.sections.uncertainties")}
        />
      </div>

      {/* Action items */}
      <ActionItemsList items={recommendation.actionItems} />

      {/* Bid deliverables checklist */}
      {bidDeliverables && bidDeliverables.length > 0 && (
        <BidDeliverablesCard items={bidDeliverables} onGoToExtraction={onGoToExtraction} />
      )}
    </div>
  )
}
